import { FC, useState, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/button';
// import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  // SendHorizontal,
  Edit,
  MoreHorizontal,
  Trash2,
} from 'lucide-react';
import { SaveWorkflowModal } from './save-workflow-modal';
import { useWorkflowStore } from '@/stores/workflow-store';
import { useOrgStore } from '@/stores/org-store';
import { Workflow, WorkflowDetails } from '@/types/workflow-types';
import { getIconComponent } from '../helper/icon-mapping';
import { useNotification } from '@/components/notifications';
import { updateWorkflow } from '@/services/workflow/update-workflow';
import { v4 as uuidv4 } from 'uuid';
import { remapNodesAndEdges } from '@/stores/node-store';

// Create New Workflow Modal Component
export const CreateNewWorkflowModal: FC<{
  isOpen: boolean;
  onClose: () => void;
  templates: WorkflowDetails[];
  onTemplateSelect: (templateId: string) => void;
  onEditTemplate: (templateId: string) => void;
  isLoading?: boolean;
}> = ({
  isOpen,
  onClose,
  templates,
  onTemplateSelect,
  onEditTemplate,
  isLoading = false,
}) => {
  // const [workflowDescription, setWorkflowDescription] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<WorkflowDetails | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { updateWorkflowMetadata, deleteWorkflow, getWorkflowList } =
    useWorkflowStore();
  const { currentOrg } = useOrgStore();
  const notification = useNotification();

  // Template loading is now handled by the workflow editor page

  const handleImportFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    // Reset so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;

    if (!currentOrg?.orgid) {
      notification.error('No organization found.');
      return;
    }

    try {
      setIsImporting(true);
      const fileContent = await file.text();
      const importData = JSON.parse(fileContent);

      if (
        !importData ||
        !Array.isArray(importData.nodes) ||
        !Array.isArray(importData.edges)
      ) {
        notification.error(
          'Invalid file format. Expected JSON with nodes and edges arrays.',
        );
        return;
      }

      const name =
        typeof importData.name === 'string' && importData.name.trim()
          ? importData.name.trim()
          : 'Untitled Imported Workflow';

      const { nodes: remappedNodes, edges: remappedEdges } = remapNodesAndEdges(
        importData.nodes,
        importData.edges,
      );

      const workflow: Workflow = {
        ...importData,
        id: uuidv4(),
        session_id: uuidv4(),
        publish_to_template: false,
        name,
        nodes: remappedNodes,
        edges: remappedEdges,
        createdAt: new Date().toISOString(),
      };

      const response = await updateWorkflow(workflow, currentOrg.orgid);

      if (response?.detail) {
        notification.error(response.detail || 'Failed to import workflow.');
        return;
      }

      try {
        await getWorkflowList(currentOrg.orgid);
      } catch (refreshError) {
        console.error('Failed to refresh workflow list:', refreshError);
        Sentry.captureException(refreshError, {
          extra: { context: 'worfklow-dashboard-import-workflow-refresh-list' },
        });
      }

      notification.success('Workflow imported successfully.');
      onClose();
    } catch (error) {
      if (error instanceof SyntaxError) {
        notification.error(
          'Invalid JSON file. Please select a valid workflow export file.',
        );
      } else {
        console.error('Error importing workflow:', error);
        Sentry.captureException(error, {
          extra: { context: 'worfklow-dashboard-import-workflow-file' },
        });
        notification.error('Failed to import workflow. Please try again.');
      }
    } finally {
      setIsImporting(false);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside any dropdown menu
      if (!target.closest('[data-dropdown-menu]')) {
        // Close all dropdowns
        const allMenus = document.querySelectorAll('[data-dropdown-menu]');
        allMenus.forEach((menu) => {
          (menu as HTMLElement).style.display = 'none';
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // const handleSubmit = () => {
  //   if (workflowDescription.trim()) {
  //     // Handle custom workflow description
  //     onClose();
  //     setWorkflowDescription('');
  //   }
  // };

  // create new workflow from template
  const handleTemplateSelect = (templateId: string) => {
    // The workflow editor page will handle loading the template
    onTemplateSelect(templateId);
    onClose();
  };

  // edit template workflow
  const editTemplateWorkflow = (templateId: string) => {
    // Find the template in the templates array
    const template = templates.find((t) => t.workflow_id === templateId);
    if (template && currentOrg?.orgid) {
      setEditingTemplate(template as unknown as WorkflowDetails);
      onEditTemplate(templateId);
    }
  };

  // Edit template details
  const editTemplateDetails = (templateId: string) => {
    // Find the template in the templates array
    const template = templates.find((t) => t.workflow_id === templateId);
    if (template && currentOrg?.orgid) {
      setEditingTemplate(template as unknown as WorkflowDetails);
      setIsEditModalOpen(true);
    }
  };

  const handleUpdateTemplate = async (
    newName: string,
    newDescription: string = '',
    icon: string = '',
    color: string = '',
    bgColor: string = '',
  ) => {
    if (!editingTemplate || !currentOrg?.orgid) {
      return;
    }

    try {
      await updateWorkflowMetadata(
        editingTemplate.workflow_id,
        newName,
        newDescription,
        currentOrg.orgid,
        true, // isTemplate = true for templates
        {
          icon,
          color,
          bgColor,
        },
      );
      getWorkflowList(currentOrg.orgid);
      setIsEditModalOpen(false);
      setEditingTemplate(null);
    } catch (error) {
      console.error('Error updating template:', error);
      throw error; // Re-throw to let the modal handle the error display
    }
  };

  const deleteTemplate = async (templateId: string) => {
    if (!currentOrg?.orgid) {
      return;
    }

    try {
      await deleteWorkflow(templateId, currentOrg.orgid);
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='sm:max-w-3xl p-0 bg-primary text-white [&>button]:text-white'>
        <div className='flex flex-col gap-6 p-6'>
          <DialogHeader>
            <div className='flex items-center justify-between text-white dark:border-border'>
              <div>
                <DialogTitle className='text-xl font-semibold text-left'>
                  Create a New Workflow
                </DialogTitle>
                <p className='text-sm mt-1 text-white'>
                  Choose a template below.
                </p>
              </div>
            </div>
          </DialogHeader>
        </div>

        {/* Workflow Templates Grid */}
        <div className='px-5 py-6 bg-[#F4F4F5] overflow-y-auto max-h-[40vh] dark:bg-popover rounded-b-lg'>
          {isLoading ? (
            <div className='flex flex-col items-center justify-center py-16 px-4 min-h-[200px] w-full'>
              <div className='w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4'></div>
              <p className='text-gray-500 dark:text-gray-400'>
                Loading templates...
              </p>
            </div>
          ) : (
            <div className='grid grid-cols-3 gap-2'>
              {templates.map((template) => {
                return (
                  <div
                    key={template.workflow_id}
                    className={`relative p-4 border border-gray-200 rounded-lg text-left bg-white hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer group dark:bg-muted dark:border-border ${isImporting && template.workflow_id === 'import-from-file' ? 'opacity-70 pointer-events-none' : ''}`}
                    onClick={() => {
                      if (template.workflow_id === 'import-from-file') {
                        if (isImporting) return;
                        fileInputRef.current?.click();
                        return;
                      }
                      // Close any open dropdowns first
                      const allMenus = document.querySelectorAll(
                        '[data-dropdown-menu]',
                      );
                      allMenus.forEach((menu) => {
                        (menu as HTMLElement).style.display = 'none';
                      });
                      handleTemplateSelect(template.workflow_id);
                    }}
                  >
                    {/* Only show dropdown menu options for non-system templates (blank, import-from-file) */}
                    {template.workflow_id !== 'blank' &&
                      template.workflow_id !== 'import-from-file' && (
                        <div className='absolute top-2 right-2'>
                          <Button
                            variant='ghost'
                            size='sm'
                            className='h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity hover:text-black hover:bg-gray-100 dark:hover:bg-gray-700 text-black dark:text-white'
                            onClick={(e) => {
                              e.stopPropagation();
                              // Close all other dropdowns first
                              const allMenus = document.querySelectorAll(
                                '[data-dropdown-menu]',
                              );
                              allMenus.forEach((menu) => {
                                (menu as HTMLElement).style.display = 'none';
                              });
                              // Simple toggle - you can implement state management here if needed
                              const menu = e.currentTarget
                                .nextElementSibling as HTMLElement;
                              if (menu) {
                                menu.style.display =
                                  menu.style.display === 'block'
                                    ? 'none'
                                    : 'block';
                              }
                            }}
                          >
                            <MoreHorizontal className='h-3 w-3' />
                          </Button>
                          <div
                            className='absolute top-6 right-0 bg-popover border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50 w-48 hidden'
                            style={{ display: 'none' }}
                            onClick={(e) => e.stopPropagation()}
                            data-dropdown-menu
                          >
                            <div className='py-1'>
                              <button
                                className='w-full px-3 py-2 text-left text-sm text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center'
                                onClick={() => {
                                  editTemplateWorkflow(template.workflow_id);
                                  // Hide menu
                                  const menu = document.querySelector(
                                    `[data-menu-for="${template.workflow_id}"]`,
                                  ) as HTMLElement;
                                  if (menu) menu.style.display = 'none';
                                }}
                              >
                                <Edit className='h-4 w-4 mr-2' />
                                Edit Template
                              </button>
                              <button
                                className='w-full px-3 py-2 text-left text-sm text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center'
                                onClick={() => {
                                  editTemplateDetails(template.workflow_id);
                                  // Hide menu
                                  const menu = document.querySelector(
                                    `[data-menu-for="${template.workflow_id}"]`,
                                  ) as HTMLElement;
                                  if (menu) menu.style.display = 'none';
                                }}
                              >
                                <Edit className='h-4 w-4 mr-2' />
                                Edit Details
                              </button>
                              <button
                                className='w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center'
                                onClick={() => {
                                  deleteTemplate(template.workflow_id);
                                  // Hide menu
                                  const menu = document.querySelector(
                                    `[data-menu-for="${template.workflow_id}"]`,
                                  ) as HTMLElement;
                                  if (menu) menu.style.display = 'none';
                                }}
                              >
                                <Trash2 className='h-4 w-4 mr-2' />
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                    <div className='flex flex-col items-start space-y-2'>
                      <div
                        className='w-12 h-12 rounded-lg flex items-center justify-center text-white group-hover:scale-105 transition-transform'
                        style={{
                          backgroundColor: template.bg_color || '#3B82F6',
                          color: template.color || '#FFFFFF',
                        }}
                      >
                        {isImporting &&
                        template.workflow_id === 'import-from-file' ? (
                          <div className='w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin' />
                        ) : (
                          getIconComponent(
                            template.icon || 'WorkflowIcon',
                            template.color || '#FFFFFF',
                            'w-6 h-6',
                          )
                        )}
                      </div>
                      <div>
                        <h4 className='font-medium text-sm text-gray-900 dark:text-white'>
                          {template.name}
                        </h4>
                        <p className='text-xs text-gray-600 mt-1 leading-relaxed dark:text-white'>
                          {template.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>

      {/* Hidden file input for import-from-file */}
      <input
        ref={fileInputRef}
        type='file'
        accept='.json'
        style={{ display: 'none' }}
        onChange={handleImportFileSelect}
      />

      {/* Edit Template Modal */}
      <SaveWorkflowModal
        isModalOpen={isEditModalOpen}
        setIsModalOpen={setIsEditModalOpen}
        isExistingWorkflow={true}
        currentWorkflowName={editingTemplate?.name || ''}
        currentWorkflowDescription={editingTemplate?.description || ''}
        currentIcon={editingTemplate?.icon}
        currentColor={editingTemplate?.color}
        currentBgColor={editingTemplate?.bg_color}
        onSave={handleUpdateTemplate}
        mode='editTemplateInfo'
      />
    </Dialog>
  );
};
